import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import Lottie from 'lottie-react';
import { AddOutlined } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import searchEmptyAnimation from '../../../assets/animations/search_empty.json';
import SearchFormControl from '../../SearchFormControl/SearchFormControl';
import './GeneralHeading.scss';

export interface GeneralHeadingProps {
  displayEmptySearch: boolean;
  heading: string;
  emptyItemsCaption?: ReactNode;
  // Omit both to hide the add button (read-only list pages with no create action).
  buttonCaption?: string;
  buttonLink?: string;
  enablSearch?: boolean;
  searchPromotedOnly?: boolean;
  isLoading?: boolean;
  initDone?: boolean;
  searchTitle?: string;
  onSearchTitleChange?: (input: string) => void;
  searchSaleOnly?: boolean;
  onSearchSaleOnlyChange?: (searchSaleOnly: boolean) => void;
  onSearchPromotedOnlyChange?: (searchPromotedOnly: boolean) => void;
}

export const GeneralHeading = ({
  displayEmptySearch,
  heading,
  emptyItemsCaption,
  buttonCaption,
  buttonLink,
  enablSearch = false,
  searchPromotedOnly = false,
  searchSaleOnly = false,
  initDone = false,
  isLoading = false,
  searchTitle,
  onSearchTitleChange,
  onSearchPromotedOnlyChange,
  onSearchSaleOnlyChange
}: GeneralHeadingProps): ReactElement => {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const [searchInput, setSearchInput] = useState(searchTitle ?? '');

  useEffect(() => {
    onSearchTitleChange?.(searchInput);
  }, [searchInput]);

  // A hash names a place on the page, e.g. the account menu's Ausstattung that
  // the shop page's "Bearbeiten" leads to; this scroll undid that.
  useEffect(() => {
    if (initDone && !hash) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [initDone, hash]);

  const handleSearchPromotedChange = (): void => {
    onSearchPromotedOnlyChange?.(!searchPromotedOnly);
  };

  const handleSearchSaleChange = (): void => {
    onSearchSaleOnlyChange?.(!searchSaleOnly);
  };

  return (
    <>
      <Box className='general-heading'>
        <Box className='general-heading__title-row'>
          <Typography variant='h5' className='general-heading__title'>
            {heading}
          </Typography>
        </Box>

        {enablSearch && (
          <Box className='general-heading__search'>
            <SearchFormControl value={searchInput} onDebouncedChange={setSearchInput} />
            <Box className='general-heading__search-filters'>
              <FormControlLabel
                className='general-heading__filter'
                control={
                  <Checkbox checked={searchPromotedOnly} onChange={handleSearchPromotedChange} />
                }
                label={t('generalHeading.topProducts', 'Top-Produkte')}
              />
              <FormControlLabel
                className='general-heading__filter'
                control={<Checkbox checked={searchSaleOnly} onChange={handleSearchSaleChange} />}
                label={t('generalHeading.saleProducts', 'SALE-Produkte')}
              />
            </Box>
          </Box>
        )}

        {displayEmptySearch && emptyItemsCaption && !isLoading && (
          <Box className='general-heading__empty'>
            <Lottie animationData={searchEmptyAnimation} className='general-heading__animation' />
            <Typography className='general-heading__empty-caption'>{emptyItemsCaption}</Typography>
          </Box>
        )}

        {buttonLink && (
          <Box className='general-heading__actions'>
            <Button
              startIcon={<AddOutlined />}
              color='primary'
              variant='contained'
              component={Link}
              to={buttonLink}>
              {buttonCaption}
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};
